<script setup lang="ts">
import { computed } from "vue";

import { linkActions } from "@/action/linkActions";
import { cardKey, cardMetaKey } from "@/components/StickyNote/injectKeys";
import { boardKey } from "@/components/board/injectKeys";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { BoardCoordinate } from "@/model/coordinates";
import { useDraggingStore } from "@/store/dragging";
import { useLinkStore } from "@/store/link";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { injectStrict } from "@/utils/vue";

import { findDeleteIconPos } from "./utils";

const props = defineProps<{ scale: number; offset: BoardCoordinate }>();

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);
const isDragging = computed(() => {
  const dragging = useDraggingStore().dragging;
  return !!dragging[card.id] || card.linkedStickies.some((link) => !!dragging[link.stickyId]);
});
const board = injectStrict(boardKey);
const links = computed(() => {
  return card.linkedStickies.flatMap((link) => {
    const matches = useSearchMenuStore().matchesLinkStates(link, true);
    if (matches) {
      const linkedCard = useLinkStore().boardCardByLink(link.stickyId, board.value);
      if (linkedCard) {
        const deleteIconPos = findDeleteIconPos(
          cardMeta.pos,
          props.scale,
          linkedCard.meta.pos,
          props.offset,
        );
        if (deleteIconPos) {
          return { linkId: link.linkId, ...deleteIconPos, to: link.stickyId };
        }
      }
    }
    return [];
  });
});

const removeLink = (link: (typeof links.value)[number]) => {
  void linkActions.remove("link-remove-button", link.linkId, useLinkStore().linksById[link.linkId]);
};
</script>

<template>
  <template v-if="!isDragging && !cardMeta.isReadonly">
    <SvgIcon
      v-for="link in links"
      :key="link.linkId"
      name="system/minus-circled"
      data-no-screenshot
      data-no-animate
      width="2em"
      height="2em"
      class="link-remove"
      :style="{ left: link.x + 'px', top: link.y + 'px' }"
      :title="$t('linkModal.removeLink')"
      @click.stop="removeLink(link)"
    />
  </template>

  <div
    v-if="!isDragging && !cardMeta.isReadonly"
    class="link-drag"
    data-no-screenshot
    data-no-animate
    aria-hidden="true"
  >
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 17">
      <circle cx="5" cy="12" r="4.5" stroke="currentColor" />
      <path fill="currentColor" d="M4.5 0h1v8h-1z" />
    </svg>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.link-remove {
  position: absolute;
  margin-left: -1em;
  margin-top: -1em;
  cursor: pointer;
  color: colors.$destructive;
  background-color: colors.$background-ui;
  border-radius: 50%;
}

.link-drag {
  pointer-events: all;
  cursor: grabbing;
  position: absolute;
  left: 45%;
  width: 20px;
  bottom: -38px;
  color: colors.$main;

  svg {
    pointer-events: none;
  }

  .sticky-note-progress ~ & {
    bottom: -49px;
  }
}
</style>
